/**
 * FAQ Schema Custom Element
 *
 * Usage:
 *   <faq-schema target-id="faq-container-id"></faq-schema>
 *
 * This element generates and injects FAQPage schema.org JSON-LD
 * based on question/answer pairs found in the specified container.
 *
 * Available attribute overrides:
 *   - target-id: The id of the container element to scan for FAQ items.
 *   - You can override selectors by setting the following attributes:
 *       item: Selector for FAQ items (default: .faq-section_content)
 *       question: Selector for question (default: .faq-question)
 *       answer: Selector for answer (default: .faq-text)
 *
 * Example:
 *   <faq-schema target-id="faq-section"></faq-schema>
 *
 * FAQ items should be structured as:
 *   <div class="faq-section_content">
 *     <div class="faq-question">Question?</div>
 *     <div class="faq-text">Answer.</div>
 *   </div>
 */

class FAQSchemaGenerator extends HTMLElement {
  constructor() {
    super();

    this.schemaScript = null;
    this.targetId = null;
    this.itemSelector = ".faq-section_content";
    this.questionSelector = ".faq-question";
    this.answerSelector = ".faq-text";
    this.scriptDelayMs = 10;
    this._mutationObserver = null;
  }

  connectedCallback() {
    this.targetId = this.getAttribute("target-id");

    if (!this.targetId) return;

    if (this.getAttribute("item"))
      this.itemSelector = this.getAttribute("item");

    if (this.getAttribute("question"))
      this.questionSelector = this.getAttribute("question");

    if (this.getAttribute("answer"))
      this.answerSelector = this.getAttribute("answer");

    // Store the timer id for cleanup
    this._initTimer = setTimeout(() => {
      this._observeTarget();
      this._updateSchemaScript();
      this._initTimer = null;
    }, this.scriptDelayMs);
  }

  disconnectedCallback() {
    this._removeSchemaScript();

    // Clear any pending timer
    if (this._initTimer) {
      clearTimeout(this._initTimer);
      this._initTimer = null;
    }

    this._removeSchemaScript();
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
  }

  /**
   * Observe the target container for DOM changes.
   */
  _observeTarget() {
    const target = document.getElementById(this.targetId);
    if (!target) return;

    this._mutationObserver = new MutationObserver(() => {
      this._updateSchemaScript();
    });

    this._mutationObserver.observe(target, { childList: true, subtree: true });
  }

  /**
   * Generate and inject the FAQPage schema script.
   */
  _updateSchemaScript() {
    const target = document.getElementById(this.targetId);
    if (!target) return;

    const items = target.querySelectorAll(this.itemSelector);

    if (!items?.length) {
      this._removeSchemaScript();
      return;
    }

    const schema = this._buildSchema(items);
    this._injectSchemaScript(schema);
    this._hideSelf();
  }

  /**
   * Build the FAQPage schema object from DOM items.
   */
  _buildSchema(items) {
    const mainEntity = [];

    items.forEach((item) => {
      const questionElement = item.querySelector(this.questionSelector);
      const question = questionElement?.innerText?.trim();

      const answerElement = item.querySelector(this.answerSelector);
      const answer = answerElement?.textContent?.trim();

      if (question && answer) {
        mainEntity.push({
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        });
      }
    });

    if (mainEntity.length === 0) {
      return null;
    }

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    };
  }

  /**
   * Inject the schema script into <head>.
   */
  _injectSchemaScript(schema) {
    this._removeSchemaScript();

    if (!schema) {
      return;
    }
    
    this.schemaScript = document.createElement("script");
    this.schemaScript.type = "application/ld+json";
    this.schemaScript.setAttribute("data-faq-schema", this.targetId || "true");
    this.schemaScript.text = JSON.stringify(schema);

    document.head.appendChild(this.schemaScript);
  }

  /**
   * Remove the schema script from <head>.
   */
  _removeSchemaScript() {
    if (this.schemaScript && this.schemaScript.parentNode) {
      this.schemaScript.parentNode.removeChild(this.schemaScript);
      this.schemaScript = null;
    } else {
      // Remove any script with this target id
      const prevScript = document.head.querySelector(
        `script[data-faq-schema="${this.targetId}"]`,
      );

      if (prevScript) prevScript.remove();
    }
  }

  /**
   * Hide the custom element from the DOM.
   */
  _hideSelf() {
    this.style.display = "none";
  }
}

customElements.define("faq-schema", FAQSchemaGenerator);
